/**
 * useFirstVisit - Auto-show the FMEA creation wizard ONLY for genuinely empty projects
 *
 * Previous behaviour opened the wizard on any fresh localStorage (first visit),
 * which meant a fully-populated project (e.g. PRO-302) got the full-screen
 * "Welcome to FMEAstudio" modal slapped over it on every fresh session.
 *
 * New gating: auto-open only when
 *   - the user hasn't dismissed the wizard, AND
 *   - the active project is genuinely empty (zero requirements AND zero
 *     components — which implies zero failure modes, since failure modes
 *     hang off components), OR there is no project at all yet.
 *
 * The wizard remains reachable on demand via startWizard() (e.g. a "new
 * project" action); this only fixes the auto-open trigger.
 *
 * fix/request-storm-loop — this hook used to call `requirementsApi.list()`
 * and `getComponents()` directly with a raw axios/fetch client on every
 * mount, completely bypassing the react-query cache that `useRequirements`/
 * `useComponents` already populate for the SAME project data elsewhere on
 * the very same page (RequirementsManager, useCanonicalComponents, etc). On
 * a cold load of a page that also renders one of those, that was a
 * guaranteed-redundant extra round trip to `/api/requirements` and
 * `/api/components` — part of the cold-load request storm on Functions/
 * FMEA/Requirements. Reading through the shared hooks instead means this
 * check rides the existing cached query (or, worst case, shares the
 * in-flight de-duped request) instead of firing its own.
 */

import { useEffect } from 'react';
import { useWizard } from '../contexts/WizardContext';
import { useProject } from '../contexts/ProjectContext';
import { useRequirements } from './useRequirements';
import { useComponents } from './useComponents';

const DISMISSED_KEY = 'fmeastudio_wizard_dismissed';

export function useFirstVisit() {
  // fix/request-storm-loop — read `state` too so we can check `isActive`
  // before calling `startWizard()` again. Belt-and-braces on top of the
  // WizardContext.tsx memoization fix: even with a stable `startWizard`
  // reference, this effect's `!activeProject` branch legitimately re-runs
  // every time ProjectContext finishes loading with no active project (e.g.
  // a real backend outage/rate-limit that never resolves `activeProject`),
  // and it used to call `startWizard()` unconditionally on every one of
  // those re-runs — resetting an already-open wizard back to its first step
  // on every fire. Guarding on `isActive` makes this idempotent regardless
  // of how many times the effect re-fires.
  const { startWizard, state: wizardState } = useWizard();
  const { activeProject, isLoading } = useProject();

  // Shared, cached queries (see file header) — `enabled` follows
  // `activeProject` so these stay dormant until there's a project to check.
  const {
    data: requirements,
    isLoading: isLoadingRequirements,
    isError: isErrorRequirements,
  } = useRequirements(activeProject ? { projectId: activeProject.id } : undefined);
  const {
    data: components,
    isLoading: isLoadingComponents,
    isError: isErrorComponents,
  } = useComponents(undefined, activeProject?.id);

  useEffect(() => {
    // Wait for the project context to finish loading before deciding.
    if (isLoading) return;

    // Respect a persisted dismissal — never auto-open once dismissed.
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return;

    // Wizard is already open (whether we opened it or the user did) — don't
    // re-trigger. See comment above: this is what makes repeated effect
    // fires (unstable deps, retries, a project fetch that never resolves)
    // safe instead of an endless startWizard() → re-render → re-fire loop.
    if (wizardState.isActive) return;

    // No project at all yet → genuinely empty workspace, guide the user.
    if (!activeProject) {
      startWizard();
      return;
    }

    // Otherwise, only open when the active project has no data. Wait for
    // both queries to settle rather than reacting mid-flight.
    if (isLoadingRequirements || isLoadingComponents) return;

    // On error, do NOT auto-open — better to stay out of the user's way
    // than to block a populated app on a transient fetch failure (429,
    // network blip, etc). The queries' own retry/cache handles recovery.
    if (isErrorRequirements || isErrorComponents) return;

    const isEmptyProject = (requirements?.length ?? 0) === 0 && (components?.length ?? 0) === 0;
    if (isEmptyProject) {
      startWizard();
    }
  }, [
    isLoading,
    activeProject,
    startWizard,
    wizardState.isActive,
    isLoadingRequirements,
    isLoadingComponents,
    isErrorRequirements,
    isErrorComponents,
    requirements,
    components,
  ]);
}
